// CVI-R013–R017: evaluation responsibility evidence and applicability.
// These pure functions intentionally do not persist or invent workflow data.
// Callers must supply real, period-scoped assignments from the backend.
export const EVALUATION_RESPONSIBILITY_IMPORTANCE = "VERY_HIGH";
export const EVALUATION_RESPONSIBILITY_STATUS = Object.freeze({
  NOT_APPLICABLE: "NOT_APPLICABLE",
  PENDING: "PENDING",
  EVALUATED: "EVALUATED",
  EXEMPT: "EXEMPT",
});

const eligible = task => task && task.affectsOthers === true &&
  task.assigned === true && task.cancelled !== true &&
  task.type !== "SELF_ASSESSMENT";

// Assignment is counted regardless of whether a review is late. Never classify
// missing API data as NOT_APPLICABLE; only an authoritative empty list can do that.
export function summarizeEvaluationResponsibility(assignments, { exempt = false } = {}) {
  if (!Array.isArray(assignments)) {
    return { status: "PENDING", evidenceAvailable: false, assignedWorkload: null,
      completionRate: null, overdueResponsibility: null, canRate: false };
  }
  const tasks = assignments.filter(eligible);
  if (exempt) return { status: "EXEMPT", evidenceAvailable: true,
    assignedWorkload: tasks.length, completionRate: null,
    overdueResponsibility: null, canRate: false };
  if (!tasks.length) return { status: "NOT_APPLICABLE", evidenceAvailable: true,
    assignedWorkload: 0, completionRate: null, overdueResponsibility: 0, canRate: false };
  const onTime = tasks.filter(t => t.completedAt && t.dueAt &&
    Date.parse(t.completedAt) <= Date.parse(t.dueAt)).length;
  const overdue = tasks.filter(t => t.dueAt &&
    Date.parse(t.dueAt) < Date.now() &&
    (!t.completedAt || Date.parse(t.completedAt) > Date.parse(t.dueAt))).length;
  return { status: "PENDING", evidenceAvailable: true,
    assignedWorkload: tasks.length, completionRate: tasks.length ? 100 * onTime / tasks.length : null,
    overdueResponsibility: overdue, canRate: true };
}

// Weight redistribution is confined to this same Core Responsibilities level.
// Pending items preserve their configured weight. Rated zero is a valid score.
export function effectiveCoreWeights(items) {
  const applicable = items.filter(item => item.status !== "NOT_APPLICABLE" && item.status !== "EXEMPT");
  const total = applicable.reduce((sum, item) => sum + Math.max(0, Number(item.weight) || 0), 0);
  return items.map(item => ({
    ...item,
    effectiveWeight: total > 0 && item.status !== "NOT_APPLICABLE" && item.status !== "EXEMPT"
      ? 100 * Math.max(0, Number(item.weight) || 0) / total : 0,
  }));
}

const count = value => (Number.isFinite(Number(value)) && value !== null ? Number(value) : null);

// What the manager panel may claim, from the GET /api/evaluation-responsibility response alone.
// - A failed or missing request is "unavailable": never N/A, never zero workload.
// - Without verified sources (sourcesComplete !== true) nothing is applicable, exempt or rateable,
//   and no metric is shown: the registry may be partial.
// - "evaluated" needs a stored rating whose evidence still matches (evidenceCurrent); a rating
//   recorded against different evidence is "stale" and is not honoured by scoring.
export function responsibilityPanelState({ loading = false, error = null, data = null } = {}) {
  if (loading) return { kind: "loading", canRate: false, metrics: null, assessment: null };
  if (error || !data || typeof data !== "object") return { kind: "unavailable", canRate: false, metrics: null, assessment: null };
  const assessment = data.assessment?.levelId ? data.assessment : null;
  if (data.sourcesComplete !== true) {
    return { kind: "verification", canRate: false, metrics: null, assessment,
      pendingSources: Array.isArray(data.incompleteSources) ? data.incompleteSources.length : null };
  }
  const evaluation = data.evaluation || {};
  const metrics = {
    assignedWorkload: count(evaluation.assignedWorkload),
    dueCount: count(evaluation.dueCount),
    onTimeCount: count(evaluation.onTimeCount),
    completionRate: count(evaluation.completionRate),
    overdue: count(evaluation.overdueResponsibility),
    completedLate: count(evaluation.completedLateCount),
  };
  if (evaluation.status === EVALUATION_RESPONSIBILITY_STATUS.EXEMPT) return { kind: "exempt", canRate: false, metrics, assessment };
  if (evaluation.status === EVALUATION_RESPONSIBILITY_STATUS.NOT_APPLICABLE) return { kind: "not_applicable", canRate: false, metrics, assessment };
  const canRate = evaluation.canRate === true;
  if (assessment && assessment.evidenceCurrent === false) return { kind: "stale", canRate, metrics, assessment };
  return { kind: assessment ? "evaluated" : "pending", canRate, metrics, assessment };
}
